import { getDefaultEarlyLimit } from '@sancare/typescript-commons/src/misc/date-utils'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import {
  mapFlatAppSettings,
  mapFlatSancareSettings
} from '@sancare/typescript-commons/src/settings/mapper'
import _ from 'lodash'

import i18n from '@/i18n'
import Api from '@/misc/api'
import { CountryEnum, ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

import { DEFAULTS } from '../stay-list/storeDefaultState'

const appSettingNameList = [
  'automation_mode_dr_threshold',
  'standard_mode_dp_threshold',
  'standard_mode_dr_threshold',
  'standard_mode_das_threshold',
  'new_coding_das_threshold',
  'standard_mode_hint_threshold',
  'standard_mode_dp_financial_threshold',
  'automation_session_financial_risk',
  'automation_non_session_financial_risk',
  'automation_ambu_financial_risk',
  'automation_non_ambu_financial_risk',
  'optimizable_stays_show_low_das',
  'optimizable_stays_show_excluded_das',
  'optimizable_stays_show_coded_das_found_by_ai',
  'new_coding_stays_show_low_das',
  'new_coding_stays_show_excluded_das',
  'automation_mode_dr_min_confidence',
  'ai_confidence_external_flag_threshold',
]
const sancareSettingNameList = [
  'enable_smr',
  'enable_dialyse',
  'enable_pseudonymization',
  'enable_data_downgrading',
  'enable_manual_acts_external_export',
  'enable_ml_negation_detection',
  'enable_comments_external_export',
  'enable_ai_suggestions',
  'demo_mode',
  'hide_sancare_searches_details',
  'session_automation',
  'non_session_automation',
  'rules_automation',
  'renewal_automation',
  'restricted_legal_finess',
  'restricted_geo_finess',
  'dialysis_medical_units',
]
const initialUrl = new URL(location.href)
const sp = new URLSearchParams(initialUrl.search)
let initialEarlyLimit
let initialLateLimit
let initialMedicalUnits
let initialHospitalizationTypes
if (sp.get('el') && dayjs(sp.get('el'), 'DD-MM-YYYY').isValid()) {
  initialEarlyLimit = dayjs(sp.get('el'), 'DD-MM-YYYY')
  sessionStorage.setItem('earlyLimit', initialEarlyLimit.format())
} else if (sessionStorage.getItem('earlyLimit') && dayjs(sessionStorage.getItem('earlyLimit')).isValid()) {
  initialEarlyLimit = dayjs(sessionStorage.getItem('earlyLimit'))
} else {
  sessionStorage.removeItem('earlyLimit')
  initialEarlyLimit = getDefaultEarlyLimit()
}
if (sp.get('ll') && dayjs(sp.get('ll'), 'DD-MM-YYYY').isValid()) {
  initialLateLimit = dayjs(sp.get('ll'), 'DD-MM-YYYY')
  sessionStorage.setItem('lateLimit', initialLateLimit.format())
} else if (sessionStorage.getItem('lateLimit') && dayjs(sessionStorage.getItem('lateLimit')).isValid()) {
  initialLateLimit = dayjs(sessionStorage.getItem('lateLimit'))
} else {
  sessionStorage.removeItem('lateLimit')
  initialLateLimit = dayjs()
}
// 2024-10-31: um key is deprecated, use mu instead
initialMedicalUnits = sp.getAll('um')
if (initialMedicalUnits.length === 0) {
  initialMedicalUnits = sp.getAll('mu')
}
if (initialMedicalUnits.length > 0) {
  sessionStorage.setItem('medicalUnits', JSON.stringify(initialMedicalUnits))
} else if (sessionStorage.getItem('medicalUnits')) {
  initialMedicalUnits = JSON.parse(sessionStorage.getItem('medicalUnits'))
} else {
  sessionStorage.removeItem('medicalUnits')
  initialMedicalUnits = null
}

initialHospitalizationTypes = sp.getAll('hospitalizationTypes')
if (initialHospitalizationTypes.length > 0) {
  sessionStorage.setItem('hospitalizationTypes', JSON.stringify(initialHospitalizationTypes))
} else if (sessionStorage.getItem('hospitalizationTypes')) {
  initialHospitalizationTypes = JSON.parse(sessionStorage.getItem('hospitalizationTypes'))
} else {
  sessionStorage.removeItem('hospitalizationTypes')
  initialHospitalizationTypes = DEFAULTS().STAYLIST_HOSPITALIZATION_TYPE
}

export default {
  state: {
    userSettings: {},
    noSearchWarning: dayjs.unix(localStorage.getItem('noSearchWarning') || 0),
    appSettings: localStorage.getItem('appSettings') ? JSON.parse(localStorage.getItem('appSettings')) : {},
    sancareSettings: localStorage.getItem('sancareSettings') ? JSON.parse(localStorage.getItem('sancareSettings')) : {},
    connectorSettings: localStorage.getItem('connectorSettings') ? JSON.parse(localStorage.getItem('connectorSettings')) : {},
    pmsi: {
      country: CountryEnum.FR,
      pmsiType: PmsiTypeEnum.MCO,
      mode: ModeEnum.OPTIMIZABLE,
    },
    facilityName: '',
    documentServiceEnable: false,

    earlyLimit: initialEarlyLimit,
    lateLimit: initialLateLimit,
    medicalUnits: [],
    selectedMedicalUnits: Array.isArray(initialMedicalUnits) ? initialMedicalUnits.map((mu) => Number(mu)) : initialMedicalUnits,
    hospitalizationTypes: Array.isArray(initialHospitalizationTypes) ? initialHospitalizationTypes.map((ht) => Number(ht)) : initialHospitalizationTypes,
    setAppSettingsRequest: { fetching: false, error: null, ok: false },
    getUserSettingsRequest: { fetching: false, error: null, ok: false },
    setUserSettingsRequest: { fetching: false, error: null, ok: false },
    setSancareSettingsRequest: { fetching: false, error: null, ok: false },
    fetchSettingsRequest: { fetching: false, error: null, ok: false },
  },
  getters: {
    flatAppSettings(state) {
      return mapFlatAppSettings(state.appSettings)
    },
    flatSancareSettings(state) {
      return mapFlatSancareSettings(state.sancareSettings)
    },
    dialysisMedicalUnitIds(state, getters) {
      return (getters.flatSancareSettings['dialysis_medical_units'] ?? []).map((unit) => unit.id)
    },
    dialysisMedicalUnits(state, getters) {
      return state.medicalUnits
        .map((facility) => ({
          ...facility,
          children: facility.children
            .map((division) => ({
              ...division,
              children: division.children.filter((unit) => getters.dialysisMedicalUnitIds.includes(unit.value)),
            }))
            .filter((division) => division.children.length > 0),
        }))
        .filter((facility) => facility.children.length > 0)
    },
    earlyLimit(state) {
      return state.earlyLimit
    },
    lateLimit(state) {
      return state.lateLimit
    },
    settingsUrlQuery(state) {
      const formattedQuery = {
        el: state.earlyLimit.format('DD-MM-YYYY'),
        ll: state.lateLimit.format('DD-MM-YYYY'),
      }
      if (state.selectedMedicalUnits !== null) {
        formattedQuery.mu = state.selectedMedicalUnits
      }
      if (state.pmsiType === PmsiTypeEnum.SMR && state.hospitalizationTypes !== null) {
        formattedQuery.hospitalizationTypes = state.hospitalizationTypes
      }

      return formattedQuery
    },
    pmsiType: (state) => state.pmsi.pmsiType,
  },
  mutations: {
    setAppSettingsStart(state) {
      state.setAppSettingsRequest = { fetching: true, error: null, ok: false }
    },
    setAppSettingsSuccess(state, newSettings) {
      state.setAppSettingsRequest = { fetching: false, error: null, ok: true }
      state.appSettings = _.mapValues(newSettings, (settingCategory) => _.pick(settingCategory, appSettingNameList))
      localStorage.setItem('appSettings', JSON.stringify(state.appSettings))
    },
    setSancareSettingsStart(state) {
      state.setSancareSettingsRequest = { fetching: true, error: null, ok: false }
    },
    setSancareSettingsSuccess(state, newSettings) {
      state.setSancareSettingsRequest = { fetching: false, error: null, ok: true }
      state.sancareSettings = _.mapValues(newSettings, (settingCategory) => _.pick(settingCategory, sancareSettingNameList))
      localStorage.setItem('sancareSettings', JSON.stringify(state.sancareSettings))
    },
    setSancareSettingsError(state, res) {
      state.setSancareSettingsRequest = { fetching: false, error: res.message, ok: false }
    },
    setAppSettingsError(state, res) {
      state.setAppSettingsRequest = { fetching: false, error: res.message, ok: false }
    },
    getUserSettingsStart(state) {
      state.getUserSettingsRequest = { fetching: true, error: null, ok: false }
    },
    getUserSettingsSuccess(state, userSettings) {
      state.getUserSettingsRequest = { fetching: false, error: null, ok: true }
      state.userSettings = { ...state.userSettings }

      _.forEach(userSettings, (setting) => {
        state.userSettings[setting.name] = setting.value
      })
    },
    getUserSettingsError(state, res) {
      state.getUserSettingsRequest = { fetching: false, error: res.message, ok: false }
    },
    setUserSettingsStart(state) {
      state.setUserSettingsRequest = { fetching: true, error: null, ok: false }
    },
    setUserSettingsSuccess(state, userSettings) {
      state.setUserSettingsRequest = { fetching: false, error: null, ok: true }
      // Replace, don't merge: the backend omits settings emptied to [], which would keep their old value
      state.userSettings = {}

      _.forEach(userSettings, (setting) => {
        state.userSettings[setting.name] = setting.value
      })
    },
    setUserSettingsError(state, res) {
      state.setUserSettingsRequest = { fetching: false, error: res.message, ok: false }
    },
    fetchSettingsStart(state) {
      state.fetchSettingsRequest = { fetching: true, error: null, ok: false }
    },
    fetchSettingsSuccess(state, settings) {
      state.fetchSettingsRequest = { fetching: false, error: null, ok: true }
      state.pmsi.country = settings.systemSettings.pmsiCountry
      i18n.global.locale.value = settings.systemSettings.pmsiCountry
      state.facilityName = settings.systemSettings.facilityName
      state.documentServiceEnable = settings.systemSettings.documentServiceEnable
      state.appSettings = _.mapValues(settings.appSettings, (settingCategory) => _.pick(settingCategory, appSettingNameList))
      state.sancareSettings = _.mapValues(settings.appSettings, (settingCategory) => _.pick(settingCategory, sancareSettingNameList))
      state.connectorSettings = settings.connectorSettings
      localStorage.setItem('appSettings', JSON.stringify(state.appSettings))
      localStorage.setItem('sancareSettings', JSON.stringify(state.sancareSettings))
      localStorage.setItem('connectorSettings', JSON.stringify(state.connectorSettings))
    },
    fetchSettingsError(state, res) {
      state.fetchSettingsRequest = { fetching: false, error: res.message, ok: false }
    },
    setEarlyLimit(state, lim) {
      state.earlyLimit = lim
      sessionStorage.setItem('earlyLimit', lim.format())
    },
    setLateLimit(state, lim) {
      state.lateLimit = lim
      sessionStorage.setItem('lateLimit', lim.format())
    },
    initMedicalUnits(state, { medicalUnits, facilities, divisions }) {
      state.medicalUnits = _.sortBy(facilities.map((facility) => {
        return {
          value: facility.id,
          label: facility.name === '' ? (`Etablissement inconnu${facility.geographicFiness ? (` (${facility.geographicFiness})`) : ''}`) : facility.name,
          children: _.sortBy(divisions
            .filter((division) => division.facility.id === facility.id)
            .map((division) => ({
              value: division.id,
              label: division.title === '' ? 'Pôle inconnu' : division.title,
              children: _.sortBy(medicalUnits
                .filter((unit) => unit.facilityDivision.id === division.id)
                .map((unit) => ({ value: unit.id, label: unit.reference + (unit.title ? ` - ${unit.title}` : '')  })), ['reference'])
            })), ['title'])
        }
      }), ['name'])
    },
    setSelectedMedicalUnits(state, medicalUnitList) {
      if (medicalUnitList && medicalUnitList.length === 0) {
        medicalUnitList = null
      }
      state.selectedMedicalUnits = medicalUnitList
      sessionStorage.setItem('medicalUnits', JSON.stringify(medicalUnitList))
    },
    setPmsiType(state, pmsiType) {
      state.pmsi.pmsiType = pmsiType
    },
    initPmsiMode(state) {
      if (state.pmsi.pmsiType === PmsiTypeEnum.SMR || state.pmsi.country === CountryEnum.CH) {
        state.pmsi.mode = ModeEnum.QUALITY_STAYS
      } else {
        state.pmsi.mode = ModeEnum.OPTIMIZABLE
      }
    },
    setPmsiMode(state, mode) {
      state.pmsi.mode = mode
    },
    setHospitalizationTypes(state, hospitalizationTypes) {
      if (hospitalizationTypes?.length === 0) {
        hospitalizationTypes = null
      }
      state.hospitalizationTypes = hospitalizationTypes
      sessionStorage.setItem('hospitalizationTypes', JSON.stringify(hospitalizationTypes))
    },
  },
  actions: {
    getSelf({ commit }) {
      return Api.get(
        {
          url: '/api/user/me',
          startMut: 'getSelfStart',
          successMut: 'getSelfSuccess',
          errorMut: 'getSelfError',
          cannotBeDelayed: true,
          doNotRetry: true,
        },
        commit
      )
    },
    updateSelf({ commit }, data) {
      return Api.put(
        {
          url: '/api/user/me',
          body: data,
          startMut: 'updateSelfStart',
          successMut: 'updateSelfSuccess',
          errorMut: 'updateSelfError',
        },
        commit
      )
    },
    setAppSettings({ commit }, settings) {
      return Api.put(
        {
          url: '/api/settings/app-settings',
          body: { ...settings },
          startMut: 'setAppSettingsStart',
          successMut: 'setAppSettingsSuccess',
          errorMut: 'setAppSettingsError',
        },
        commit
      )
    },
    setSancareSettings({ commit }, settings) {
      return Api.put(
        {
          url: '/api/settings/sancare-settings',
          body: { ...settings },
          startMut: 'setSancareSettingsStart',
          successMut: 'setSancareSettingsSuccess',
          errorMut: 'setSancareSettingsError',
        },
        commit
      )
    },
    getUserSettings({ commit }) {
      return Api.get(
        {
          url: '/api/settings/user-settings',
          startMut: 'getUserSettingsStart',
          successMut: 'getUserSettingsSuccess',
          errorMut: 'getUserSettingsError',
        },
        commit
      )
    },
    setUserSettings({ commit }, userSettings) {
      return Api.post(
        {
          url: '/api/settings/user-settings',
          body: userSettings,
          startMut: 'setUserSettingsStart',
          successMut: 'setUserSettingsSuccess',
          errorMut: 'setUserSettingsError',
        },
        commit
      )
    },
    fetchSettings({ commit }) {
      return Api.get(
        {
          url: '/api/settings',
          startMut: 'fetchSettingsStart',
          successMut: 'fetchSettingsSuccess',
          errorMut: 'fetchSettingsError',
        },
        commit
      )
    },
  },
}
